import { Reducer, combineReducers, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { persistStore } from 'redux-persist';

import { RESET_STATE_ACTION_TYPE } from '@app/store/actions/reset-state';
import { API_SLICES } from '@app/store/api-slices';
import { authApi } from '@app/store/auth/api';
import authSlice from '@app/store/auth/slice';
import { couponCodeApi } from '@app/store/coupon-code/api';
import formSlice from '@app/store/forms/slice';
import { integrationApi } from '@app/store/integration-apis';
import { plansApi } from '@app/store/plans/api';
import { formsApi } from '@app/store/redux/form-api';
import { importApi } from '@app/store/redux/import-api';
import { openFormulierenImportApi } from '@app/store/redux/open-formulieren-import-api';
import { pdfImportApi } from '@app/store/redux/pdf-import-api';
import { templatesApi } from '@app/store/redux/template-api';
import { templateApi } from '@app/store/template/api';
import { workspacesApi } from '@app/store/workspaces/api';
import { membersNInvitationsApi } from '@app/store/workspaces/members-n-invitations-api';
import workspaceSlice from '@app/store/workspaces/slice';

import { analyticsApi } from './analytics/api';
import { apiActionsApi } from './api-actions-api';
import { mediaLibraryApi } from './media-library/api';
import mutationStatusSlice from './mutations/slice';
import { priceSuggestionApi } from './price-suggestion/api';
import { mfaRequiredRedirect } from './mfa-required-redirect';
import { ssoRequiredRedirect } from './sso-required-redirect';

// Every API slice's middleware, from the one list (store/api-slices.ts).
const middlewares = API_SLICES.map((api) => api.middleware);

// if (environments.IS_IN_PRODUCTION_MODE) middlewares.splice(0, 1);

const reducers = {
    [mutationStatusSlice.reducerPath]: mutationStatusSlice.reducer,
    [authSlice.reducerPath]: authSlice.reducer,
    [formSlice.reducerPath]: formSlice.reducer,
    [workspaceSlice.reducerPath]: workspaceSlice.reducer,
    [authApi.reducerPath]: authApi.reducer,
    [membersNInvitationsApi.reducerPath]: membersNInvitationsApi.reducer,
    [plansApi.reducerPath]: plansApi.reducer,
    [workspacesApi.reducerPath]: workspacesApi.reducer,
    [apiActionsApi.reducerPath]: apiActionsApi.reducer,
    [templateApi.reducerPath]: templateApi.reducer,
    [couponCodeApi.reducerPath]: couponCodeApi.reducer,
    [priceSuggestionApi.reducerPath]: priceSuggestionApi.reducer,
    [mutationStatusSlice.reducerPath]: mutationStatusSlice.reducer,
    [formsApi.reducerPath]: formsApi.reducer,
    [templatesApi.reducerPath]: templatesApi.reducer,
    [importApi.reducerPath]: importApi.reducer,
    [pdfImportApi.reducerPath]: pdfImportApi.reducer,
    [openFormulierenImportApi.reducerPath]: openFormulierenImportApi.reducer,
    [mediaLibraryApi.reducerPath]: mediaLibraryApi.reducer,
    [analyticsApi.reducerPath]: analyticsApi.reducer,
    [integrationApi.reducerPath]: integrationApi.reducer
    // A new API slice also goes into API_SLICES (store/api-slices.ts), or
    // the sign-out reset misses it (reset-after-sign-out.test.tsx fails).
};

const combinedReducer = combineReducers<typeof reducers>(reducers);

export const rootReducer: Reducer<RootState> = (state, action) => {
    if (action.type === RESET_STATE_ACTION_TYPE) {
        state = {} as RootState;
    }
    return combinedReducer(state, action);
};

export const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: false
            // RTK 2's Tuple.concat needs the middlewares spread (an array
            // argument widens the tuple type and fails to typecheck).
        }).concat(...middlewares, ssoRequiredRedirect, mfaRequiredRedirect)
});

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof combinedReducer>;

setupListeners(store.dispatch);
